import { useState, useCallback, useEffect } from 'react'
import { Sheet, Typography, Stack, Alert, IconButton, LinearProgress, Tooltip, Button, Tabs, TabList, Tab, TabPanel } from '@mui/joy'
import { Box } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import PersonIcon from '@mui/icons-material/Person'
import GroupsIcon from '@mui/icons-material/Groups'
import FileDownloadIcon from '@mui/icons-material/FileDownload'

import { useTelegramAccounts } from './hooks/useTelegramAccounts'
import { useTelegramGroups } from './hooks/useTelegramGroups'
import { AccountsTable } from './components/AccountsTable'
import { GroupsTable } from './components/GroupsTable'
import { SignInModal, ManageWatchModal, ManageGroupModal, ImportGroupsModal, AddGroupModal } from './components/Modals'

// Constants
const ALERT_AUTO_DISMISS_MS = 5000 // 5 seconds

export default function TelegramAccountsPage(): JSX.Element {
    const {
        message,
        error,
        setMessage,
        setError,
        resetAlerts,
        accounts,
        watchedMap,
        loading,
        loggingOutPhone,
        watcherActionPhone,
        fetchAccounts,
        handleLogout,
        handleStartWatcher,
        handleStopWatcher,
        updateWatchedMap,
    } = useTelegramAccounts()

    const {
        groups,
        loading: groupsLoading,
        message: groupsMessage,
        error: groupsError,
        setMessage: setGroupsMessage,
        setError: setGroupsError,
        resetAlerts: resetGroupsAlerts,
        fetchGroups,
        handleDeleteGroup,
    } = useTelegramGroups()

    // Tab state
    const [activeTab, setActiveTab] = useState(0)

    // Sign-in Modal state
    const [openSignin, setOpenSignin] = useState(false)
    const [initialPhone, setInitialPhone] = useState('')

    // Manage Watch Modal state
    const [openWatch, setOpenWatch] = useState(false)
    const [watchPhone, setWatchPhone] = useState('')

    // Manage Group Modal state
    const [openManageGroup, setOpenManageGroup] = useState(false)
    const [manageGroupPeerId, setManageGroupPeerId] = useState('')

    // Import Groups Modal state
    const [openImportGroups, setOpenImportGroups] = useState(false)

    // Add Group Modal state
    const [openAddGroup, setOpenAddGroup] = useState(false)

    const openSigninModal = useCallback(() => {
        resetAlerts()
        setInitialPhone('')
        setOpenSignin(true)
    }, [resetAlerts])

    const openWatchModalFor = useCallback(
        (phone: string) => {
            resetAlerts()
            setWatchPhone(phone)
            setOpenWatch(true)
        },
        [resetAlerts]
    )

    const handleReconnect = useCallback(
        (phone: string) => {
            setInitialPhone(phone)
            setOpenSignin(true)
            resetAlerts()
        },
        [resetAlerts]
    )

    const handleSignInSuccess = useCallback(async () => {
        await fetchAccounts()
        setMessage('Logged in successfully.')
    }, [fetchAccounts, setMessage])

    const openManageGroupModal = useCallback(
        (peerId: string) => {
            resetGroupsAlerts()
            setManageGroupPeerId(peerId)
            setOpenManageGroup(true)
        },
        [resetGroupsAlerts]
    )

    const openImportGroupsModal = useCallback(() => {
        resetGroupsAlerts()
        setOpenImportGroups(true)
    }, [resetGroupsAlerts])

    const openAddGroupModal = useCallback(() => {
        resetGroupsAlerts()
        setOpenAddGroup(true)
    }, [resetGroupsAlerts])

    const handleGroupUpdate = useCallback(async () => {
        await fetchGroups()
    }, [fetchGroups])

    const handleDeleteGroupAction = useCallback(
        async (peerId: string) => {
            await handleDeleteGroup(peerId)
        },
        [handleDeleteGroup]
    )

    // Auto-dismiss alerts for accounts
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                setMessage(null)
            }, ALERT_AUTO_DISMISS_MS)
            return () => clearTimeout(timer)
        }
    }, [message, setMessage])

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError(null)
            }, ALERT_AUTO_DISMISS_MS)
            return () => clearTimeout(timer)
        }
    }, [error, setError])

    // Auto-dismiss alerts for groups
    useEffect(() => {
        if (groupsMessage) {
            const timer = setTimeout(() => {
                setGroupsMessage(null)
            }, ALERT_AUTO_DISMISS_MS)
            return () => clearTimeout(timer)
        }
    }, [groupsMessage, setGroupsMessage])

    useEffect(() => {
        if (groupsError) {
            const timer = setTimeout(() => {
                setGroupsError(null)
            }, ALERT_AUTO_DISMISS_MS)
            return () => clearTimeout(timer)
        }
    }, [groupsError, setGroupsError])

    // Get current alerts based on active tab
    const currentMessage = activeTab === 0 ? message : groupsMessage
    const currentError = activeTab === 0 ? error : groupsError
    const currentSetMessage = activeTab === 0 ? setMessage : setGroupsMessage
    const currentSetError = activeTab === 0 ? setError : setGroupsError
    const currentLoading = activeTab === 0 ? loading : groupsLoading

    return (
        <Box sx={{ minHeight: '100dvh', bgcolor: 'background.body', p: { xs: 2, md: 3 } }}>
            <Sheet
                variant="outlined"
                sx={{
                    width: '100%',
                    mx: 'auto',
                    p: { xs: 2, sm: 3 },
                    borderRadius: 'xl',
                    boxShadow: 'sm',
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography level="h4">Telegram Watcher</Typography>
                </Stack>

                {currentLoading && <LinearProgress thickness={2} sx={{ mb: 2 }} />}
                {currentMessage && (
                    <Alert
                        color="success"
                        variant="soft"
                        sx={{ mb: 2 }}
                        endDecorator={
                            <IconButton size="sm" variant="plain" color="success" onClick={() => currentSetMessage(null)}>
                                <CloseIcon />
                            </IconButton>
                        }
                    >
                        {currentMessage}
                    </Alert>
                )}
                {currentError && (
                    <Alert
                        color="danger"
                        variant="soft"
                        sx={{ mb: 2 }}
                        endDecorator={
                            <IconButton size="sm" variant="plain" color="danger" onClick={() => currentSetError(null)}>
                                <CloseIcon />
                            </IconButton>
                        }
                    >
                        {currentError}
                    </Alert>
                )}

                <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)} sx={{ bgcolor: 'transparent' }}>
                    <TabList
                        disableUnderline
                        sx={{
                            p: 0.5,
                            gap: 0.5,
                            borderRadius: 'xl',
                            bgcolor: 'background.level1',
                            [`& .MuiTab-root`]: {
                                flex: 'none',
                                fontWeight: 'md',
                                [`&.Mui-selected`]: {
                                    bgcolor: 'background.surface',
                                    boxShadow: 'sm',
                                },
                            },
                        }}
                    >
                        <Tab disableIndicator value={0}>
                            <PersonIcon sx={{ mr: 1 }} />
                            Accounts
                        </Tab>
                        <Tab disableIndicator value={1}>
                            <GroupsIcon sx={{ mr: 1 }} />
                            Groups
                        </Tab>
                    </TabList>

                    <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
                        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                            <Tooltip title="Add account">
                                <Button variant="solid" color="primary" startDecorator={<AddIcon />} onClick={openSigninModal}>
                                    Add Account
                                </Button>
                            </Tooltip>
                        </Stack>

                        <AccountsTable
                            accounts={accounts}
                            watchedMap={watchedMap}
                            loggingOutPhone={loggingOutPhone}
                            watcherActionPhone={watcherActionPhone}
                            onLogout={handleLogout}
                            onReconnect={handleReconnect}
                            onManageWatch={openWatchModalFor}
                            onStartWatcher={handleStartWatcher}
                            onStopWatcher={handleStopWatcher}
                        />
                    </TabPanel>

                    <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
                            <Tooltip title="Import groups from a connected account">
                                <Button variant="soft" color="neutral" startDecorator={<FileDownloadIcon />} onClick={openImportGroupsModal}>
                                    Import from Account
                                </Button>
                            </Tooltip>
                            <Tooltip title="Add group manually">
                                <Button variant="solid" color="primary" startDecorator={<AddIcon />} onClick={openAddGroupModal}>
                                    Add Group
                                </Button>
                            </Tooltip>
                        </Stack>

                        <GroupsTable groups={groups} loading={groupsLoading} onManageGroup={openManageGroupModal} onDeleteGroup={handleDeleteGroupAction} />
                    </TabPanel>
                </Tabs>
            </Sheet>

            <SignInModal
                open={openSignin}
                accounts={accounts}
                initialPhone={initialPhone}
                onClose={() => setOpenSignin(false)}
                onSuccess={handleSignInSuccess}
                onError={setError}
                onMessage={setMessage}
            />

            <ManageWatchModal
                open={openWatch}
                phone={watchPhone}
                accounts={accounts}
                onClose={() => setOpenWatch(false)}
                onWatchedMapUpdate={updateWatchedMap}
            />

            <ManageGroupModal
                open={openManageGroup}
                peerId={manageGroupPeerId}
                accounts={accounts}
                onClose={() => setOpenManageGroup(false)}
                onUpdate={handleGroupUpdate}
            />

            <ImportGroupsModal open={openImportGroups} accounts={accounts} onClose={() => setOpenImportGroups(false)} onImport={handleGroupUpdate} />

            <AddGroupModal open={openAddGroup} accounts={accounts} onClose={() => setOpenAddGroup(false)} onCreate={handleGroupUpdate} />
        </Box>
    )
}
